import { FormEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import {
  deletePublicFormDraftAdmin,
  fetchMyFormSubmissionDetail,
  fetchMyFormSubmissions,
  fetchPublicForm,
  fetchPublicFormDraft,
  fetchPublicRelationOptions,
  sendPublicFormBackup,
  submitPublicForm,
  uploadPublicFormFile,
  PublicFormField,
  PublicFormSubmitMeta
} from "../api/publicFormsApi";
import { authAPI } from "../api/authApi";
import { useAuth } from "../auth/AuthContext";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "react-toastify";
import { MdOutlineFileUpload } from "react-icons/md";
import RichTextEdit from "../editors/RichTextEdit";
import {
  PUBLIC_FORM_SUBMISSION_OUTCOME_KEY,
  PublicFormSubmissionOutcomeState
} from "./publicFormSubmissionOutcome";
import { getRelationOptionLabelFromField } from "./publicFormRelationLabel";
import { ItemSelectorField } from "@/components/ItemSelectorDialog";
import { LinkFieldDisplay } from "@/components/LinkFieldDisplay";
import { LinkFieldEditor, findFirstLinkFieldValidationError } from "../component/linkFieldEditor";
import { isLinkFieldType } from "@/utils/linkFieldValue";

const PUBLIC_FORM_FILE_ACCEPT = ".pdf,.txt,.docx";
const PUBLIC_FORM_FILE_EXT = [".pdf", ".txt", ".docx"];

type RelationOptions = Record<string, any[]>;

const PUBLIC_FORM_DRAFT_KEY = 'publicFormDraft';

const publicFormClientSessionKey = (t: string) => `publicFormClientSession:${t}`;
const publicFormUnfinishedUidKey = (t: string) => `publicFormUnfinishedUid:${t}`;

function filterDataToCurrentForm(
  data: Record<string, any>,
  fields: PublicFormField[] | undefined
): Record<string, any> {
  if (!Array.isArray(fields)) {
    return {};
  }
  const currentFieldNames = new Set(fields.map((field) => field.name));
  return Object.fromEntries(
    Object.entries(data).filter(([name]) => currentFieldNames.has(name))
  );
}

function getOrCreateClientSessionId(formToken: string): string {
  if (typeof sessionStorage === 'undefined') {
    return '';
  }
  const k = publicFormClientSessionKey(formToken);
  let id = sessionStorage.getItem(k);
  if (!id) {
    id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    sessionStorage.setItem(k, id);
  }
  return id;
}

function readUnfinishedUid(formToken: string | null): string | null {
  if (!formToken || typeof sessionStorage === 'undefined') {
    return null;
  }
  return sessionStorage.getItem(publicFormUnfinishedUidKey(formToken));
}

function persistUnfinishedUid(formToken: string | null, uid: string | undefined) {
  if (!formToken || typeof sessionStorage === 'undefined' || !uid) {
    return;
  }
  sessionStorage.setItem(publicFormUnfinishedUidKey(formToken), uid);
}

function clearUnfinishedUid(formToken: string | null) {
  if (!formToken || typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.removeItem(publicFormUnfinishedUidKey(formToken));
}

function isEditableSubmissionPolicy(policy: string | undefined): boolean {
  return policy === 'overwrite' || policy === 'duplicate';
}

const isEmptyValue = (value: any) => value === undefined || value === null || value === '';

function collectMissingRequiredFields(
  fields: PublicFormField[],
  data: Record<string, any>
): PublicFormField[] {
  const missing: PublicFormField[] = [];
  for (const field of fields) {
    const value = data[field.name];
    if (field.type === 'checkbox') {
      if (value !== true) {
        missing.push(field);
      }
      continue;
    }
    if (isEmptyValue(value)) {
      missing.push(field);
    }
  }
  return missing;
}

const normalizeEmail = (value: string) => value.trim().toLowerCase();
const isValidEmailFormat = (value: string) =>
  !!value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const parseSelectOptions = (value: any) => {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value
      .map((option) => String(option).trim())
      .filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((option) => option.trim())
      .filter(Boolean);
  }
  return [];
};

function publicFormSelectOptionValue(option: unknown): string {
  if (option == null) return "";
  if (typeof option === "object" && option !== null && "value" in option && (option as { value: unknown }).value != null) {
    return String((option as { value: unknown }).value);
  }
  return String(option);
}

function getPublicFormRelationFields(form: { fields?: PublicFormField[] } | null): PublicFormField[] {
  return (form?.fields || []).filter(
    (field) => field.type === 'relation' || field.type === 'multiplerelation'
  );
}

function shouldLoadPublicFormRelationOptions(
  readOnly: boolean,
  form: { submissionPolicy?: string } | null,
  hasInitialSubmissionData: boolean
): boolean {
  if (!form || getPublicFormRelationFields(form).length === 0) {
    return false;
  }
  if (!readOnly) {
    return true;
  }
  return isEditableSubmissionPolicy(form.submissionPolicy) && hasInitialSubmissionData;
}

async function fetchPublicFormRelationOptionsMap(
  form: { fields?: PublicFormField[] },
  token: string,
  ownerSubmissionUid?: string | null
): Promise<RelationOptions> {
  const relationFields = getPublicFormRelationFields(form);
  const nextOptions: RelationOptions = {};
  await Promise.all(
    relationFields.map(async (field) => {
      const relatedSlug = typeof field.value === 'string' ? field.value : field.table ?? field.name;
      if (!relatedSlug) {
        nextOptions[field.name] = [];
        return;
      }
      try {
        const items = await fetchPublicRelationOptions(token, relatedSlug, {
          submissionUid: ownerSubmissionUid ?? undefined
        });
        nextOptions[field.name] = Array.isArray(items) ? items : [];
      } catch {
        nextOptions[field.name] = [];
      }
    })
  );
  return nextOptions;
}

function PublicFormRelationSelect({
  field,
  value,
  options,
  loading,
  onChange
}: {
  field: PublicFormField;
  value: any;
  options: any[];
  loading: boolean;
  onChange: (field: PublicFormField, value: any) => void;
}) {
  const isMulti = field.type === 'multiplerelation';
  const columnHeader = field.label || field.name || 'Option';

  const selectorItems = useMemo(() => {
    const sorted = [...options].sort((a: any, b: any) => {
      const aLabel = getRelationOptionLabelFromField(field, a);
      const bLabel = getRelationOptionLabelFromField(field, b);
      return aLabel.localeCompare(bLabel, undefined, { sensitivity: 'base', numeric: true });
    });
    const rows = sorted
      .filter((option: any) => option?.uid != null && String(option.uid).trim() !== '')
      .map((option: any) => {
        const label = getRelationOptionLabelFromField(field, option);
        return {
          value: String(option.uid),
          label,
          cells: { label },
          data: option,
        };
      });
    if (!isMulti) {
      rows.unshift({
        value: '',
        label: loading ? 'Loading...' : 'Not selected',
        cells: { label: loading ? 'Loading...' : 'Not selected' },
        data: null,
      });
    }
    return rows;
  }, [options, field, isMulti, loading]);

  const selectorValue = isMulti
    ? (Array.isArray(value) ? value.map(String) : value != null && value !== '' ? [String(value)] : [])
    : (value != null && value !== '' ? String(value) : '');

  return (
    <ItemSelectorField
      variant="publicForm"
      multiple={isMulti}
      items={selectorItems}
      value={selectorValue}
      loading={loading}
      disabled={loading}
      title={columnHeader}
      placeholder={loading ? 'Loading...' : 'Not selected'}
      tableColumns={[{ key: 'label', header: columnHeader }]}
      emptyMessage="No options match your search."
      onChange={(next) => {
        if (isMulti) {
          const arr = Array.isArray(next) ? next.map(String).filter(Boolean) : [];
          onChange(field, arr);
          return;
        }
        const v = typeof next === 'string' ? next : Array.isArray(next) ? next[0] ?? '' : '';
        onChange(field, v);
      }}
    />
  );
}

function PublicFormSelectInput({
  field,
  sortedOptions,
  value,
  onChange,
}: {
  field: PublicFormField;
  sortedOptions: unknown[];
  value: string;
  onChange: (field: PublicFormField, value: string) => void;
}) {
  const allowEmpty = (field as { allowEmptyOption?: boolean }).allowEmptyOption !== false;
  const firstVal = useMemo(() => {
    if (!sortedOptions.length) return undefined;
    return publicFormSelectOptionValue(sortedOptions[0]);
  }, [sortedOptions]);

  useEffect(() => {
    if (allowEmpty) return;
    if (firstVal === undefined) return;
    if ((value ?? "") !== "") return;
    onChange(field, firstVal);
  }, [allowEmpty, firstVal, value, field, onChange]);

  const displayValue =
    !allowEmpty && (value === "" || value == null) && firstVal !== undefined
      ? firstVal
      : value ?? "";

  return (
    <select
      className="w-full border rounded px-3 py-2 text-sm"
      value={displayValue}
      onChange={(event) => onChange(field, event.target.value)}
    >
      {allowEmpty && <option value="">Select</option>}
      {sortedOptions.map((option: any) => (
        <option key={option.value ?? option} value={option.value ?? option}>
          {option.label ?? option}
        </option>
      ))}
    </select>
  );
}

const capitalizeStart = (token: string) => {
  if (!token) return token;
  return `${token.charAt(0).toUpperCase()}${token.slice(1)}`;
};

const toTitleCase = (value: string) => {
  return value
    .split(/(\s+)/)
    .map((part) => {
      if (!part || /^\s+$/.test(part)) {
        return part;
      }
      return part
        .split(/([\-'])/)
        .map((segment) => (segment === '-' || segment === "'" ? segment : `${segment.charAt(0).toUpperCase()}${segment.slice(1).toLowerCase()}`))
        .join('');
    })
    .join('');
};

const toNameCase = (value: string) => {
  const parts = value.split(/(\s+)/);
  const wordIndexes = parts
    .map((part, index) => ({ part, index }))
    .filter(({ part }) => part && !/^\s+$/.test(part))
    .map(({ index }) => index);

  if (wordIndexes.length === 0) {
    return value;
  }

  const firstWordIndex = wordIndexes[0];
  const lastWordIndex = wordIndexes[wordIndexes.length - 1];

  return parts
    .map((part, index) => {
      if (!part || /^\s+$/.test(part)) {
        return part;
      }
      if (index !== firstWordIndex && index !== lastWordIndex) {
        return part;
      }
      return part
        .split(/([\-'])/)
        .map((segment) => (segment === '-' || segment === "'" ? segment : capitalizeStart(segment)))
        .join('');
    })
    .join('');
};

const applyTextCaseRule = (value: string, caseRule?: string) => {
  switch (caseRule) {
    case 'lower':
      return value.toLowerCase();
    case 'title':
      return toTitleCase(value);
    case 'name':
      return toNameCase(value);
    default:
      return value;
  }
};

const RICH_TEXT_CLASS = '[&_p]:mb-2 [&_p:last-child]:mb-0';

/** Heading sizes for rich text (Quill header 1–4). */
const RICH_TEXT_HEADING_CLASS =
  '[&_h1]:text-xl [&_h1]:font-semibold [&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:first:mt-0 ' +
  '[&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-2 [&_h2]:first:mt-0 ' +
  '[&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1 ' +
  '[&_h4]:text-sm [&_h4]:font-semibold [&_h4]:mt-2 [&_h4]:mb-1';

/** Link styling so anchors in rich text are visible and clickable. */
const RICH_TEXT_LINK_CLASS =
  '[&_a]:text-blue-600 [&_a]:underline [&_a]:cursor-pointer hover:[&_a]:text-blue-800 [&_a]:break-all';

/** Decode HTML entities so that escaped content (e.g. &lt;p&gt;) renders as HTML. */
const decodeHtmlEntities = (raw: string): string => {
  if (typeof document === 'undefined') return raw;
  const el = document.createElement('div');
  el.innerHTML = raw;
  return el.innerHTML;
};

/** Default body copy when "Message for visitors (when closed)" is unset or only empty editor markup. */
const CLOSED_VISITOR_DEFAULT_MESSAGE =
  'This form is not accepting submissions right now.';

/** True when HTML has visible text (empty Quill blocks like `<p><br></p>` count as empty). */
function richTextHasVisibleContent(html: unknown): boolean {
  if (html == null || typeof html !== 'string') {
    return false;
  }
  const normalized = html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return normalized.length > 0;
}

const renderInstructionContent = (htmlValue: unknown) => {
  const html = typeof htmlValue === 'string' ? htmlValue : '';
  if (!html.trim()) {
    return null;
  }
  return (
    <div
      className={`text-sm text-gray-700 ${RICH_TEXT_CLASS} ${RICH_TEXT_HEADING_CLASS} ${RICH_TEXT_LINK_CLASS}`}
      dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(html) }}
    />
  );
};

const renderRichText = (htmlValue: unknown, className: string) => {
  const html = typeof htmlValue === 'string' ? htmlValue : '';
  if (!html.trim()) {
    return null;
  }
  return (
    <div
      className={`${className} ${RICH_TEXT_CLASS} ${RICH_TEXT_HEADING_CLASS} ${RICH_TEXT_LINK_CLASS}`}
      dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(html) }}
    />
  );
};

export type PublicFormProps = {
  readOnly?: boolean;
  initialData?: Record<string, any>;
  formToken?: string;
  embedded?: boolean;
  /** Loads closed + edit-mode forms with fields when the user is editing from My submissions. */
  ownerSubmissionUid?: string;
  /** Resolved labels for relation fields (read-only display; editable forms keep UIDs in initialData). */
  relationDisplay?: Record<string, string>;
};

const PublicForm = (props: PublicFormProps = {}) => {
  const {
    readOnly = false,
    initialData,
    formToken: formTokenProp,
    embedded = false,
    ownerSubmissionUid,
    relationDisplay: relationDisplayProp
  } = props;
  const { token: tokenFromParams } = useParams();
  const token = formTokenProp ?? tokenFromParams ?? null;
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState<any>(null);
  const [formClosed, setFormClosed] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [relationOptions, setRelationOptions] = useState<RelationOptions>({});
  const [relationOptionsLoading, setRelationOptionsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEmailGate, setShowEmailGate] = useState(false);
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [autoSubmitHandled, setAutoSubmitHandled] = useState(false);
  const [fileUploadingField, setFileUploadingField] = useState<string | null>(null);
  const [rateLimitUntil, setRateLimitUntil] = useState<number | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [showWaitMessage, setShowWaitMessage] = useState(false);
  const [hasPriorSubmission, setHasPriorSubmission] = useState(false);

  const prefillDoneRef = useRef<string | null>(null);
  const missingRequiredToastKeyRef = useRef<string | null>(null);

  const hasInitialSubmissionData = Boolean(
    initialData && typeof initialData === 'object' && Object.keys(initialData).length > 0
  );

  const effectiveReadOnly = useMemo(() => {
    if (!readOnly) {
      return false;
    }
    if (form && isEditableSubmissionPolicy(form.submissionPolicy) && hasInitialSubmissionData) {
      return false;
    }
    return true;
  }, [readOnly, form, form?.submissionPolicy, hasInitialSubmissionData]);

  const submitPrimaryLabel = useMemo(() => {
    if (!form) return 'Submit';
    if (form.submissionPolicy !== 'overwrite') return 'Submit';
    if (
      ownerSubmissionUid ||
      (readOnly && hasInitialSubmissionData) ||
      hasPriorSubmission
    ) {
      return 'Update';
    }
    return 'Submit';
  }, [form, ownerSubmissionUid, readOnly, hasInitialSubmissionData, hasPriorSubmission]);

  const goToSubmissionDone = useCallback(
    (apiMessage: string) => {
      if (!token || !form) return;
      localStorage.removeItem(PUBLIC_FORM_DRAFT_KEY);
      clearUnfinishedUid(token);
      const updated = apiMessage === 'Submission updated';
      const payload: PublicFormSubmissionOutcomeState = {
        token,
        formTitle: form.title ?? null,
        updated,
        successMessageHtml: form.successMessage ?? null,
        apiMessage
      };
      try {
        sessionStorage.setItem(PUBLIC_FORM_SUBMISSION_OUTCOME_KEY, JSON.stringify(payload));
      } catch {
        /* ignore */
      }
      const path = location.pathname.startsWith('/public/forms/')
        ? `/public/forms/${token}/submitted`
        : `/forms/${token}/submitted`;
      navigate(path, { replace: true, state: payload });
    },
    [token, form, navigate, location.pathname]
  );

  const loadForm = useCallback(async () => {
    if (!token) {
      const msg = 'Missing form token';
      setError(msg);
      toast.error(msg);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setFormClosed(false);
      setHasPriorSubmission(false);
      prefillDoneRef.current = null;
      missingRequiredToastKeyRef.current = null;
      setRelationOptions({});
      setRelationOptionsLoading(false);
      const { form: data, closed } = await fetchPublicForm(
        token,
        ownerSubmissionUid ? { submissionUid: ownerSubmissionUid } : undefined
      );
      setFormClosed(closed);
      setForm(data);
      if (readOnly && initialData && Object.keys(initialData).length > 0) {
        setFormData(initialData);
      } else {
        setFormData((prev) => (Object.keys(prev).length > 0 ? prev : {}));
      }
      setError(null);
      setLoading(false);

      const hasInitialData =
        Boolean(initialData && typeof initialData === 'object' && Object.keys(initialData).length > 0);
      const needsRelationOptions = shouldLoadPublicFormRelationOptions(readOnly, data, hasInitialData);
      if (needsRelationOptions && token) {
        setRelationOptionsLoading(true);
        try {
          const options = await fetchPublicFormRelationOptionsMap(data, token, ownerSubmissionUid);
          setRelationOptions(options);
        } catch {
          setRelationOptions({});
        } finally {
          setRelationOptionsLoading(false);
        }
      }
    } catch (err: any) {
      const msg = err.message || 'Unable to load form';
      setError(msg);
      //toast.error(msg);
      setLoading(false);
      setRelationOptionsLoading(false);
    }
  }, [token, ownerSubmissionUid, readOnly, initialData]);

  useEffect(() => {
    loadForm();
  }, [loadForm, user?.uid]);

  useEffect(() => {
    if (ownerSubmissionUid) {
      setHasPriorSubmission(true);
    }
  }, [ownerSubmissionUid]);

  useEffect(() => {
    if (loading || readOnly || !form || !token || !user?.email || !user?.uid) {
      return;
    }
    if (!isEditableSubmissionPolicy(form.submissionPolicy)) {
      return;
    }
    if (searchParams.get('resume') === '1' || searchParams.get('draftId')) {
      return;
    }
    if (ownerSubmissionUid) {
      return;
    }
    const prefillKey = `${String(form.uid)}:${token}:${user.uid}`;
    if (prefillDoneRef.current === prefillKey) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rawDraft = localStorage.getItem(PUBLIC_FORM_DRAFT_KEY);
        if (rawDraft) {
          try {
            const d = JSON.parse(rawDraft) as { token?: string; data?: Record<string, unknown> };
            if (d?.token === token && d?.data && Object.keys(d.data).length > 0) {
              prefillDoneRef.current = prefillKey;
              return;
            }
          } catch {
            /* ignore */
          }
        }
        const entries = await fetchMyFormSubmissions(100);
        if (cancelled) return;
        const forForm = entries.filter((s) => s.formToken === token && !s.archived);
        if (forForm.length === 0) {
          prefillDoneRef.current = prefillKey;
          return;
        }
        setHasPriorSubmission(true);
        forForm.sort((a, b) => {
          const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return tb - ta;
        });
        const latest = forForm[0];
        const detail = await fetchMyFormSubmissionDetail(token, latest.uid);
        if (cancelled) return;
        setFormData((prev) => (Object.keys(prev).length === 0 ? detail.data : prev));
        prefillDoneRef.current = prefillKey;
      } catch {
        /* e.g. network error; allow retry on remount */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    loading,
    readOnly,
    form,
    form?.uid,
    form?.submissionPolicy,
    token,
    user?.email,
    user?.uid,
    searchParams,
    ownerSubmissionUid
  ]);

  const buildSubmitMeta = useCallback((): PublicFormSubmitMeta => {
    const clientSessionId = token ? getOrCreateClientSessionId(token) : '';
    return {
      publicFormDraftId: searchParams.get('draftId') || undefined,
      unfinishedClientSessionId: clientSessionId || undefined,
      submissionUid: ownerSubmissionUid || undefined
    };
  }, [token, searchParams, ownerSubmissionUid]);

  useEffect(() => {
    if (rateLimitUntil == null) return;
    const tick = () => {
      const secs = Math.max(0, Math.ceil((rateLimitUntil - Date.now()) / 1000));
      setRemainingSeconds(secs);
      if (secs <= 0) {
        setRateLimitUntil(null);
        setShowWaitMessage(false);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [rateLimitUntil]);

  useEffect(() => {
    if (!form) return;
    const prevTitle = document.title;
    document.title = form.title && form.title.trim() ? form.title.trim() : 'Form';
    return () => {
      document.title = prevTitle;
    };
  }, [form?.title]);

  const requiredFields = useMemo(() => {
    if (!form?.fields) {
      return [];
    }
    return form.fields.filter((field: PublicFormField) => field.required && field.type !== 'instruction');
  }, [form?.fields]);

  // Existing submissions and local drafts can still contain fields that were
  // removed from the form. Never send those stale values back on update.
  const currentFormData = useMemo(
    () => filterDataToCurrentForm(formData, form?.fields),
    [formData, form?.fields]
  );

  const restrictedEditableFields = useMemo(() => {
    const names = form?.editableFieldsOnUpdate;
    if (!Array.isArray(names) || names.length === 0) {
      return null;
    }
    const set = new Set(
      names
        .map((name: unknown) => (typeof name === 'string' ? name.trim() : ''))
        .filter(Boolean)
    );
    return set.size > 0 ? set : null;
  }, [form?.editableFieldsOnUpdate]);

  const isUpdatingSubmission = useMemo(() => {
    if (!form || !isEditableSubmissionPolicy(form.submissionPolicy)) {
      return false;
    }
    return Boolean(ownerSubmissionUid || hasPriorSubmission || hasInitialSubmissionData);
  }, [form, ownerSubmissionUid, hasPriorSubmission, hasInitialSubmissionData]);

  const isFieldLockedOnUpdate = useCallback(
    (fieldName: string) =>
      isUpdatingSubmission &&
      restrictedEditableFields != null &&
      !restrictedEditableFields.has(fieldName),
    [isUpdatingSubmission, restrictedEditableFields]
  );

  const requiredFieldsForValidation = useMemo(() => {
    if (!isUpdatingSubmission || !restrictedEditableFields) {
      return requiredFields;
    }
    return requiredFields.filter((field) => restrictedEditableFields.has(field.name));
  }, [requiredFields, isUpdatingSubmission, restrictedEditableFields]);

  const validateForm = useCallback((data: Record<string, any> = formData) => {
    const linkErr = findFirstLinkFieldValidationError(form?.fields || [], data);
    if (linkErr) {
      return linkErr;
    }
    const missing = collectMissingRequiredFields(requiredFieldsForValidation, data);
    if (missing.length === 0) {
      return null;
    }
    const field = missing[0];
    if (field.type === 'checkbox') {
      return `Field "${field.label || field.name}" must be checked`;
    }
    return `Field "${field.label || field.name}" is required`;
  }, [requiredFieldsForValidation, formData, form?.fields]);

  useEffect(() => {
    if (loading || !form || effectiveReadOnly || !isUpdatingSubmission) {
      return;
    }
    const toastKey = `${String(form.uid)}:${ownerSubmissionUid ?? 'returning'}`;
    if (missingRequiredToastKeyRef.current === toastKey) {
      return;
    }
    const missing = collectMissingRequiredFields(requiredFieldsForValidation, formData);
    if (missing.length === 0) {
      return;
    }
    missingRequiredToastKeyRef.current = toastKey;
    const labels = missing.map((field) => field.label || field.name).join(', ');
    toast.warn(
      `This form has required fields that must be filled in before you can update your submission: ${labels}.`,
      { autoClose: 8000 }
    );
  }, [
    loading,
    form,
    effectiveReadOnly,
    isUpdatingSubmission,
    ownerSubmissionUid,
    requiredFieldsForValidation,
    formData
  ]);

  const handleChange = (field: PublicFormField, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field.name]: value
    }));
  };

  const getRelationOptionLabel = getRelationOptionLabelFromField;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    if (rateLimitUntil != null && Date.now() < rateLimitUntil) {
      setShowWaitMessage(true);
      return;
    }

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      toast.error(validationError);
      return;
    }

    if (user?.email && token) {
      setSubmitting(true);
      saveDraft();
      try {
        const result = await submitPublicForm(token, currentFormData, buildSubmitMeta());
        goToSubmissionDone(result.message);
      } catch (err: any) {
        if (err?.status === 429) {
          const secs = err?.retryAfterSeconds ?? 60;
          setRateLimitUntil(Date.now() + secs * 1000);
        }
        const msg = err?.message ?? "Submission failed";
        setError(msg);
        toast.error(msg);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    saveDraft();
    if (token && Object.keys(currentFormData).length > 0) {
      const clientSessionId = getOrCreateClientSessionId(token);
      const unfinishedUid = readUnfinishedUid(token);
      void sendPublicFormBackup(token, currentFormData, {
        clientSessionId: clientSessionId || undefined,
        unfinishedUid: unfinishedUid || undefined
      }).then((r) => {
        if (r.unfinishedUid) {
          persistUnfinishedUid(token, r.unfinishedUid);
        }
      });
    }
    if (user?.email && !email) {
      setEmail(normalizeEmail(user.email));
    }
    setEmailStatus(null);
    setEmailSent(false);
    setShowEmailGate(true);
  };

  const saveDraft = useCallback(() => {
    if (!token) {
      return;
    }
    const returnPath = `/forms/${token}?resume=1`;
    const payload = {
      token,
      returnPath,
      data: currentFormData
    };
    localStorage.setItem(PUBLIC_FORM_DRAFT_KEY, JSON.stringify(payload));
  }, [currentFormData, token]);

  const handleEmailGate = async () => {
    if (rateLimitUntil != null && Date.now() < rateLimitUntil) {
      setShowWaitMessage(true);
      return;
    }
    setEmailStatus(null);
    setEmailSent(false);
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail || !normalizedEmail.includes('@')) {
      const msg = 'Enter a valid email address.';
      setEmailStatus(msg);
      toast.error(msg);
      return;
    }

    try {
      setSubmitting(true);
      saveDraft();
      const result = await authAPI.publicFormAuthInitiate(normalizedEmail, {
        returnPath: token ? `/forms/${token}?resume=1` : undefined,
        draft: token
          ? {
            token,
            data: currentFormData,
            clientSessionId: getOrCreateClientSessionId(token)
          }
          : undefined
      });
      setEmailStatus(
        result.linkType === 'login'
          ? 'We have sent you a login link. Please check your inbox (and spam folder) for an email from noreply@labwick.com to complete your submission.'
          : 'We have sent you a registration link. Please check your inbox (and spam folder) for an email from noreply@labwick.com to complete your submission.'
      );
      setEmailSent(true);
    } catch (err: any) {
      if (err?.status === 429) {
        const secs = err?.retryAfterSeconds ?? 60;
        setRateLimitUntil(Date.now() + secs * 1000);
      }
      const msg = err?.status === 429
        ? 'Too many attempts. Please wait a minute before trying again.'
        : (err.message || 'Unable to start verification');
      setEmailStatus(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (readOnly || autoSubmitHandled || !form || !user) {
      return;
    }
    const resume = searchParams.get('resume');
    if (resume !== '1') {
      return;
    }
    const draftId = searchParams.get('draftId');
    const raw = localStorage.getItem(PUBLIC_FORM_DRAFT_KEY);
    if (!raw && !draftId) {
      return;
    }

    const clearDraft = async (resolvedDraftId?: string | null) => {
      localStorage.removeItem(PUBLIC_FORM_DRAFT_KEY);
      if (!resolvedDraftId || !token) {
        return;
      }
      try {
        await deletePublicFormDraftAdmin(token, resolvedDraftId);
      } catch (err) {
        console.warn('Unable to delete public form draft', err);
      }
    };

    const submitDraft = async (draftData: Record<string, any>) => {
      const validationError = validateForm(draftData);
      if (validationError) {
        setError(validationError);
        toast.error(validationError);
        setAutoSubmitHandled(true);
        return;
      }
      const waitForToken = (retries = 10, intervalMs = 150): Promise<boolean> => {
        if (authAPI.getIdToken()) return Promise.resolve(true);
        if (retries <= 0) return Promise.resolve(false);
        return new Promise((resolve) => {
          setTimeout(() => {
            waitForToken(retries - 1, intervalMs).then(resolve);
          }, intervalMs);
        });
      };
      const hasToken = await waitForToken();
      if (!hasToken) {
        const msg = 'Session not ready. Please wait a moment and try again.';
        setError(msg);
        toast.error(msg);
        setAutoSubmitHandled(true);
        return;
      }
      setAutoSubmitHandled(true);
      try {
        const result = await submitPublicForm(token as string, draftData || {}, buildSubmitMeta());
        await clearDraft(draftId);
        goToSubmissionDone(result.message);
      } catch (err: any) {
        const msg = err.message || 'Submission failed';
        setError(msg);
        toast.error(msg);
      }
    };

    if (raw) {
      try {
        const draft = JSON.parse(raw);
        if (draft?.token !== token) {
          return;
        }
        const draftData = draft?.data || {};
        setAutoSubmitHandled(true);
        localStorage.removeItem(PUBLIC_FORM_DRAFT_KEY);
        if (draftData) {
          setFormData(draftData);
        }
        submitDraft(draftData);
      } catch (err) {
        const msg = 'Unable to resume submission';
        setError(msg);
        toast.error(msg);
      }
      return;
    }

    if (!draftId) {
      return;
    }

    setAutoSubmitHandled(true);
    fetchPublicFormDraft(draftId)
      .then((draft: { token?: string; data?: Record<string, any> }) => {
        if (draft?.token && draft.token !== token) {
          return;
        }
        const draftData = draft?.data || {};
        if (draftData) {
          setFormData(draftData);
          localStorage.setItem(PUBLIC_FORM_DRAFT_KEY, JSON.stringify({
            token,
            returnPath: `/forms/${token}?resume=1`,
            data: draftData
          }));
        }
        submitDraft(draftData);
      })
      .catch((err: any) => {
        const msg = err.message || 'Unable to resume submission';
        setError(msg);
        toast.error(msg);
      });
  }, [autoSubmitHandled, buildSubmitMeta, goToSubmissionDone, form, searchParams, token, user, validateForm]);

  if (loading) {
    const loader = (
      <div className="panel w-[320px] p-6 shadow-md">
        <h1 className="text-center mb-2">Loading form...</h1>
        <p className="text-sm text-center text-gray-500">Please wait.</p>
      </div>
    );
    if (embedded) {
      return <div className="flex items-center justify-center w-full py-8">{loader}</div>;
    }
    return (
      <div className="flex items-center justify-center w-full h-screen">
        {loader}
      </div>
    );
  }

  if (!form) {
    const errorPanel = (
      <div className="panel w-[320px] p-6 shadow-md">
        <h1 className="text-center mb-2">Form not available or closed.</h1>
      </div>
    );
    console.error(error);
    if (embedded) {
      return <div className="flex items-center justify-center w-full py-8">{errorPanel}</div>;
    }
    return (
      <div className="flex items-center justify-center w-full h-screen">
        {errorPanel}
      </div>
    );
  }

  const topBar = (
    <header className="fixed top-0 left-0 right-0 z-20 h-14 w-full border-b border-border bg-background shadow-sm shrink-0">
      <div className="w-full h-full px-4 py-3 flex items-center justify-between gap-3 max-w-4xl mx-auto">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {form.iconUrl && (
            <img
              src={form.iconUrl}
              alt=""
              className="h-8 w-8 object-contain flex-shrink-0 rounded"
            />
          )}
          {form.title && <h1 className="text-lg font-semibold truncate">{form.title}</h1>}
        </div>
        <a
          href="https://labwick.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-medium shrink-0 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline underline-offset-2 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded"
        >
          Registration by Labwick.com
        </a>
      </div>
    </header>
  );

  if (formClosed) {
    const closedRich = richTextHasVisibleContent(form.closedMessage)
      ? renderRichText(form.closedMessage, 'text-sm text-black')
      : null;
    const closedHeading =
      typeof form.closedHeader === 'string' && form.closedHeader.trim()
        ? form.closedHeader.trim()
        : 'Form closed';
    const closedPanel = (
      <div className="panel w-full max-w-[520px] p-6 shadow-md rounded-lg border bg-background space-y-3 text-left">
        <h2 className="text-2xl font-semibold">{closedHeading}</h2>
        {closedRich ?? (
          <p className="text-sm text-muted-foreground">{CLOSED_VISITOR_DEFAULT_MESSAGE}</p>
        )}
      </div>
    );
    if (embedded) {
      return (
        <div className="flex flex-col w-full gap-4 py-4">
          <div className="flex items-center gap-3 min-h-14 px-2 border-b border-border pb-3">
            {form.iconUrl && (
              <img
                src={form.iconUrl}
                alt=""
                className="h-8 w-8 object-contain flex-shrink-0 rounded"
              />
            )}
            {form.title && <h1 className="text-lg font-semibold truncate">{form.title}</h1>}
          </div>
          <div className="flex justify-center px-2">{closedPanel}</div>
        </div>
      );
    }
    return (
      <div className="fixed inset-0 flex flex-col overflow-hidden bg-muted/30">
        {topBar}
        <div className="fixed top-14 left-0 right-0 bottom-0 overflow-auto flex items-center justify-center py-10 px-4">
          {closedPanel}
        </div>
      </div>
    );
  }

  const formatReadOnlyValue = (val: unknown): string => {
    if (val === null || val === undefined || val === '') return '—';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (Array.isArray(val)) return val.map(formatReadOnlyValue).join(', ');
    if (typeof val === 'object' && val !== null && 'name' in val && typeof (val as { name: unknown }).name === 'string') {
      return (val as { name: string }).name;
    }
    return String(val);
  };

  const renderReadOnlyFieldBlock = (
    field: PublicFormField,
    containerClass: string,
    instructionBlock: ReactNode,
    value: unknown
  ) => {
    const label = field.label || field.name;
    if (field.type === 'richtext' && typeof value === 'string' && value.trim()) {
      return (
        <div key={field.name} className={containerClass}>
          {instructionBlock}
          <Label>{label}</Label>
          {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
          <div
            className={`text-sm text-gray-900 dark:text-gray-100 ${RICH_TEXT_CLASS} ${RICH_TEXT_HEADING_CLASS} ${RICH_TEXT_LINK_CLASS}`}
            dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(value) }}
          />
        </div>
      );
    }
    if (isLinkFieldType(field.type)) {
      return (
        <div key={field.name} className={containerClass}>
          {instructionBlock}
          <Label>{label}</Label>
          {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
          <LinkFieldDisplay fieldType={field.type} value={value} className="text-sm break-words" />
        </div>
      );
    }
    if (field.type === 'relation' || field.type === 'multiplerelation') {
      const options = relationOptions[field.name] || [];
      const resolveLabel = (item: unknown): string => {
        if (item == null) return '—';
        const uid =
          typeof item === 'object' && item !== null && 'uid' in item
            ? (item as { uid: string }).uid
            : String(item);
        const option = options.find((opt: any) => opt?.uid === uid || opt?.id === uid);
        if (option) {
          return getRelationOptionLabel(field, option);
        }
        const fromDisplay = relationDisplayProp?.[field.name];
        if (typeof fromDisplay === 'string' && fromDisplay.trim() && !Array.isArray(item)) {
          return fromDisplay.trim();
        }
        return typeof item === 'object' && item !== null && 'name' in item
          ? String((item as { name: unknown }).name)
          : uid;
      };
      const displayText =
        field.type === 'multiplerelation'
          ? Array.isArray(value)
            ? value.map(resolveLabel).filter(Boolean).join(', ')
            : formatReadOnlyValue(value)
          : resolveLabel(value);
      return (
        <div key={field.name} className={containerClass}>
          {instructionBlock}
          <Label>{label}</Label>
          {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
          <p className="text-sm text-gray-900 dark:text-gray-100 break-words">{displayText || '—'}</p>
        </div>
      );
    }
    return (
      <div key={field.name} className={containerClass}>
        {instructionBlock}
        <Label>{label}</Label>
        {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
        <p className="text-sm text-gray-900 dark:text-gray-100 break-words">{formatReadOnlyValue(value)}</p>
      </div>
    );
  };

  const formPanelClassName = "panel w-full max-w-[640px] p-6 shadow-md rounded-lg border bg-background space-y-5";
  const formPanel = effectiveReadOnly ? (
    <div className={formPanelClassName}>
      <div className="space-y-1">
        {form.subtitle && <h2 className="text-2xl font-semibold">{form.subtitle}</h2>}
        {(!showEmailGate || effectiveReadOnly) && renderRichText(form.description, 'text-sm text-black text-left')}
      </div>

      {!effectiveReadOnly && error && <p className="text-sm text-red-500">{error}</p>}
      {!effectiveReadOnly && showWaitMessage && remainingSeconds > 0 && (
        <p className="text-sm text-amber-600">
          Please wait {remainingSeconds} seconds before trying again.
        </p>
      )}
      {(!showEmailGate || effectiveReadOnly) && (form.fields || []).map((field: PublicFormField, index: number) => {
        const formFields: PublicFormField[] = form.fields || [];
        const previousField = index > 0 ? formFields[index - 1] : null;
        const nextField = index < formFields.length - 1 ? formFields[index + 1] : null;
        const leadingInstruction = previousField?.type === 'instruction' ? previousField : null;
        const containerClass = leadingInstruction ? "space-y-3" : "space-y-2";
        const instructionBlock = leadingInstruction ? (
          renderInstructionContent(leadingInstruction.value)
        ) : null;
        const value = formData[field.name];
        const label = field.label || field.name;
        const requiredMark = field.required ? <span className="text-red-500" aria-hidden="true"> *</span> : null;

        if ((effectiveReadOnly || isFieldLockedOnUpdate(field.name)) && field.type !== 'instruction') {
          return renderReadOnlyFieldBlock(field, containerClass, instructionBlock, value);
        }

        if (field.type === 'instruction') {
          if (nextField) {
            return null;
          }
          return (
            <div key={field.name} className="space-y-2">
              {renderInstructionContent(field.value)}
            </div>
          );
        }

        if (field.type === 'textarea') {
          return (
            <div key={field.name} className={containerClass}>
              {instructionBlock}
              <Label>{label}{requiredMark}</Label>
              {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
              <textarea
                className="w-full border rounded px-3 py-2 text-sm"
                value={value || ''}
                onChange={(event) => handleChange(field, event.target.value)}
              />
            </div>
          );
        }

        if (field.type === 'checkbox') {
          return (
            <div key={field.name} className={containerClass}>
              {instructionBlock}
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={value === true}
                  onCheckedChange={(checked) => handleChange(field, checked === true)}
                />
                <Label>{label}{requiredMark}</Label>
              </div>
            </div>
          );
        }

        if (field.type === 'select' || field.type === 'dropdown') {
          const options = field.options && Array.isArray(field.options)
            ? field.options
            : parseSelectOptions(field.value);
          const sortedOptions = [...options].sort((a: any, b: any) => {
            const aLabel = String(a?.label ?? a?.value ?? a ?? '');
            const bLabel = String(b?.label ?? b?.value ?? b ?? '');
            return aLabel.localeCompare(bLabel, undefined, { sensitivity: 'base', numeric: true });
          });
          return (
            <div key={field.name} className={containerClass}>
              {instructionBlock}
              <Label>{label}{requiredMark}</Label>
              {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
              <PublicFormSelectInput
                field={field}
                sortedOptions={sortedOptions}
                value={value || ''}
                onChange={handleChange}
              />
            </div>
          );
        }

        if (field.type === 'relation' || field.type === 'multiplerelation') {
          const options = relationOptions[field.name] || [];
          return (
            <div key={field.name} className={containerClass}>
              {instructionBlock}
              <Label>{label}{requiredMark}</Label>
              {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
              <PublicFormRelationSelect
                field={field}
                value={value}
                options={options}
                loading={relationOptionsLoading}
                onChange={handleChange}
              />
            </div>
          );
        }

        if (field.type === 'richtext') {
          return (
            <div key={field.name} className={containerClass}>
              {instructionBlock}
              <Label>{label}{requiredMark}</Label>
              {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
              <div className="min-h-[200px]">
                <RichTextEdit
                  name={field.name}
                  value={typeof value === 'string' ? value : ''}
                  onChange={(payload) => handleChange(field, payload.value)}
                />
              </div>
            </div>
          );
        }

        if (field.type === 'number') {
          const numValue = value !== undefined && value !== null && value !== '' ? Number(value) : '';
          return (
            <div key={field.name} className={containerClass}>
              {instructionBlock}
              <Label>{label}{requiredMark}</Label>
              {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
              <Input
                type="number"
                value={numValue === '' ? '' : numValue}
                onChange={(e) => {
                  const raw = e.target.value;
                  const next = raw === '' || raw === '-' ? '' : Number(raw);
                  handleChange(field, next === '' ? '' : next);
                }}
                className="w-full"
              />
            </div>
          );
        }

        if (field.type === 'date' || field.type === 'dateonly') {
          const dateValue = value && typeof value === 'string' ? value.slice(0, 10) : '';
          return (
            <div key={field.name} className={containerClass}>
              {instructionBlock}
              <Label>{label}{requiredMark}</Label>
              {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
              <Input
                type="date"
                value={dateValue}
                onChange={(e) => handleChange(field, e.target.value || '')}
                className="w-full"
              />
            </div>
          );
        }

        if (field.type === 'datetime') {
          let datetimeValue = '';
          if (value && typeof value === 'string') {
            try {
              const d = new Date(value);
              if (!isNaN(d.getTime())) {
                datetimeValue = d.toISOString().slice(0, 16);
              }
            } catch {
              datetimeValue = value.slice(0, 16);
            }
          }
          return (
            <div key={field.name} className={containerClass}>
              {instructionBlock}
              <Label>{label}{requiredMark}</Label>
              {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
              <Input
                type="datetime-local"
                value={datetimeValue}
                onChange={(e) => handleChange(field, e.target.value ? new Date(e.target.value).toISOString() : '')}
                className="w-full"
              />
            </div>
          );
        }

        if (field.type === 'timeonly') {
          const timeValue = value && typeof value === 'string' ? value.slice(0, 5) : '';
          return (
            <div key={field.name} className={containerClass}>
              {instructionBlock}
              <Label>{label}{requiredMark}</Label>
              {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
              <Input
                type="time"
                value={timeValue}
                onChange={(e) => handleChange(field, e.target.value || '')}
                className="w-full"
              />
            </div>
          );
        }

        if (field.type === 'file') {
          const filename = typeof value === 'string' ? value : '';
          const isLoading = fileUploadingField === field.name;
          const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file || !token) return;
            const ext = (file.name.includes('.') ? '.' + file.name.split('.').pop() : '').toLowerCase();
            if (!PUBLIC_FORM_FILE_EXT.includes(ext)) {
              const msg = 'Invalid file type. Only PDF, TXT and DOCX are allowed.';
              setError(msg);
              toast.error(msg);
              return;
            }
            setError(null);
            setFileUploadingField(field.name);
            uploadPublicFormFile(token, file)
              .then((res) => {
                if (res.success && res.filename) {
                  handleChange(field, res.filename);
                }
              })
              .catch((err) => {
                const msg = err?.message || 'Upload failed';
                setError(msg);
                toast.error(msg);
              })
              .finally(() => setFileUploadingField(null));
            e.target.value = '';
          };
          return (
            <div key={field.name} className={containerClass}>
              {instructionBlock}
              <Label>{label}{requiredMark}</Label>
              {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
              <div className="flex items-center gap-2 flex-wrap">
                {filename ? (
                  <>
                    <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                      {filename.split('/').pop() || filename}
                    </span>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept={PUBLIC_FORM_FILE_ACCEPT}
                        className="sr-only"
                        disabled={isLoading}
                        onChange={handleFileChange}
                      />
                      <span className="inline-flex items-center justify-center rounded-md text-sm font-medium border border-input bg-background px-3 py-2 hover:bg-accent hover:text-accent-foreground">
                        {isLoading ? 'Uploading...' : 'Replace'}
                      </span>
                    </label>
                  </>
                ) : (
                  <label className="flex items-center gap-2 cursor-pointer hover:opacity-80">
                    <input
                      type="file"
                      accept={PUBLIC_FORM_FILE_ACCEPT}
                      className="sr-only"
                      disabled={isLoading}
                      onChange={handleFileChange}
                    />
                    {isLoading ? (
                      <span className="animate-spin rounded-full h-5 w-5 border-2 border-muted-foreground border-t-transparent flex-shrink-0" />
                    ) : (
                      <MdOutlineFileUpload className="text-2xl text-muted-foreground flex-shrink-0" />
                    )}
                    <span className="text-sm text-muted-foreground">
                      {isLoading ? 'Uploading...' : 'Choose file (PDF, TXT, DOCX)'}
                    </span>
                  </label>
                )}
              </div>
            </div>
          );
        }

        if (isLinkFieldType(field.type)) {
          return (
            <div key={field.name} className={containerClass}>
              {instructionBlock}
              <Label>{label}{requiredMark}</Label>
              {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
              <LinkFieldEditor
                fieldType={field.type}
                value={value ?? ''}
                required={field.required === true}
                onChange={(next) => handleChange(field, next)}
              />
            </div>
          );
        }
        return (
          <div key={field.name} className={containerClass}>
            {instructionBlock}
            <Label>{label}{requiredMark}</Label>
            {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
            <Input
              type="text"
              value={value ?? ''}
              onChange={(event) => handleChange(field, applyTextCaseRule(event.target.value, (field as any).case))}
            />
          </div>
        );
      })}

      {!effectiveReadOnly && showEmailGate && (
        <div className="border rounded-md p-4 space-y-2 bg-background">
          <h2 className="text-sm font-semibold">Verify your email</h2>
          <p className="text-xs text-muted-foreground">
            We will send a login or registration link before you can submit. Please look for an email from noreply@labwick.com (and check your spam folder).
          </p>
          {emailStatus && (
            <Alert
              className={emailSent
                ? "border-green-200 bg-green-50 text-green-800"
                : "border-red-200 bg-red-50 text-red-700"}
            >
              <AlertDescription>{emailStatus}</AlertDescription>
            </Alert>
          )}
          {!emailSent && (
            <Input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && isValidEmailFormat(email) && !submitting) {
                  event.preventDefault();
                  handleEmailGate();
                }
              }}
            />
          )}
          {!emailSent && (
            <Button
              type="button"
              onClick={handleEmailGate}
              disabled={!isValidEmailFormat(email) || submitting}
              className={
                remainingSeconds > 0
                  ? "bg-blue-600 text-white hover:bg-blue-700 opacity-70 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }
            >
              {remainingSeconds > 0 ? "Wait" : "Send link"}
            </Button>
          )}
        </div>
      )}

      {!showEmailGate && !effectiveReadOnly && (
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={submitting}
            className={remainingSeconds > 0 ? "opacity-70 cursor-not-allowed" : ""}
          >
            {submitting
              ? "Submitting..."
              : remainingSeconds > 0
                ? "Wait"
                : submitPrimaryLabel}
          </Button>
        </div>
      )}
    </div>
  ) : (
    <form onSubmit={handleSubmit} className={formPanelClassName}>
      <div className="space-y-1">
        {form.subtitle && <h2 className="text-2xl font-semibold">{form.subtitle}</h2>}
        {!showEmailGate && renderRichText(form.description, 'text-sm text-black text-left')}
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
      {showWaitMessage && remainingSeconds > 0 && (
        <p className="text-sm text-amber-600">
          Please wait {remainingSeconds} seconds before trying again.
        </p>
      )}
      {!showEmailGate && (form.fields || []).map((field: PublicFormField, index: number) => {
        const formFields: PublicFormField[] = form.fields || [];
        const previousField = index > 0 ? formFields[index - 1] : null;
        const nextField = index < formFields.length - 1 ? formFields[index + 1] : null;
        const leadingInstruction = previousField?.type === 'instruction' ? previousField : null;
        const containerClass = leadingInstruction ? "space-y-3" : "space-y-2";
        const instructionBlock = leadingInstruction ? (
          renderInstructionContent(leadingInstruction.value)
        ) : null;
        const value = formData[field.name];
        const label = field.label || field.name;
        const requiredMark = field.required ? <span className="text-red-500" aria-hidden="true"> *</span> : null;
        if (field.type === 'instruction') {
          if (nextField) return null;
          return (
            <div key={field.name} className="space-y-2">
              {renderInstructionContent(field.value)}
            </div>
          );
        }
        if (isFieldLockedOnUpdate(field.name)) {
          return renderReadOnlyFieldBlock(field, containerClass, instructionBlock, value);
        }
        if (field.type === 'textarea') {
          return (
            <div key={field.name} className={containerClass}>
              {instructionBlock}
              <Label>{label}{requiredMark}</Label>
              {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
              <textarea
                className="w-full border rounded px-3 py-2 text-sm"
                value={value || ''}
                onChange={(event) => handleChange(field, event.target.value)}
              />
            </div>
          );
        }
        if (field.type === 'checkbox') {
          return (
            <div key={field.name} className={containerClass}>
              {instructionBlock}
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={value === true}
                  onCheckedChange={(checked) => handleChange(field, checked === true)}
                />
                <Label>{label}{requiredMark}</Label>
              </div>
            </div>
          );
        }
        if (field.type === 'select' || field.type === 'dropdown') {
          const options = field.options && Array.isArray(field.options)
            ? field.options
            : parseSelectOptions(field.value);
          const sortedOptions = [...options].sort((a: any, b: any) => {
            const aLabel = String(a?.label ?? a?.value ?? a ?? '');
            const bLabel = String(b?.label ?? b?.value ?? b ?? '');
            return aLabel.localeCompare(bLabel, undefined, { sensitivity: 'base', numeric: true });
          });
          return (
            <div key={field.name} className={containerClass}>
              {instructionBlock}
              <Label>{label}{requiredMark}</Label>
              {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
              <PublicFormSelectInput
                field={field}
                sortedOptions={sortedOptions}
                value={value || ''}
                onChange={handleChange}
              />
            </div>
          );
        }
        if (field.type === 'relation' || field.type === 'multiplerelation') {
          const options = relationOptions[field.name] || [];
          return (
            <div key={field.name} className={containerClass}>
              {instructionBlock}
              <Label>{label}{requiredMark}</Label>
              {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
              <PublicFormRelationSelect
                field={field}
                value={value}
                options={options}
                loading={relationOptionsLoading}
                onChange={handleChange}
              />
            </div>
          );
        }
        if (field.type === 'richtext') {
          return (
            <div key={field.name} className={containerClass}>
              {instructionBlock}
              <Label>{label}{requiredMark}</Label>
              {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
              <div className="min-h-[200px]">
                <RichTextEdit
                  name={field.name}
                  value={typeof value === 'string' ? value : ''}
                  onChange={(payload) => handleChange(field, payload.value)}
                />
              </div>
            </div>
          );
        }
        if (field.type === 'number') {
          const numValue = value !== undefined && value !== null && value !== '' ? Number(value) : '';
          return (
            <div key={field.name} className={containerClass}>
              {instructionBlock}
              <Label>{label}{requiredMark}</Label>
              {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
              <Input
                type="number"
                value={numValue === '' ? '' : numValue}
                onChange={(e) => {
                  const raw = e.target.value;
                  const next = raw === '' || raw === '-' ? '' : Number(raw);
                  handleChange(field, next === '' ? '' : next);
                }}
                className="w-full"
              />
            </div>
          );
        }
        if (field.type === 'date' || field.type === 'dateonly') {
          const dateValue = value && typeof value === 'string' ? value.slice(0, 10) : '';
          return (
            <div key={field.name} className={containerClass}>
              {instructionBlock}
              <Label>{label}{requiredMark}</Label>
              {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
              <Input
                type="date"
                value={dateValue}
                onChange={(e) => handleChange(field, e.target.value || '')}
                className="w-full"
              />
            </div>
          );
        }
        if (field.type === 'datetime') {
          let datetimeValue = '';
          if (value && typeof value === 'string') {
            try {
              const d = new Date(value);
              if (!isNaN(d.getTime())) {
                datetimeValue = d.toISOString().slice(0, 16);
              }
            } catch {
              datetimeValue = value.slice(0, 16);
            }
          }
          return (
            <div key={field.name} className={containerClass}>
              {instructionBlock}
              <Label>{label}{requiredMark}</Label>
              {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
              <Input
                type="datetime-local"
                value={datetimeValue}
                onChange={(e) => handleChange(field, e.target.value ? new Date(e.target.value).toISOString() : '')}
                className="w-full"
              />
            </div>
          );
        }
        if (field.type === 'timeonly') {
          const timeValue = value && typeof value === 'string' ? value.slice(0, 5) : '';
          return (
            <div key={field.name} className={containerClass}>
              {instructionBlock}
              <Label>{label}{requiredMark}</Label>
              {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
              <Input
                type="time"
                value={timeValue}
                onChange={(e) => handleChange(field, e.target.value || '')}
                className="w-full"
              />
            </div>
          );
        }
        if (field.type === 'file') {
          const filename = typeof value === 'string' ? value : '';
          const isLoading = fileUploadingField === field.name;
          const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file || !token) return;
            const ext = (file.name.includes('.') ? '.' + file.name.split('.').pop() : '').toLowerCase();
            if (!PUBLIC_FORM_FILE_EXT.includes(ext)) {
              const msg = 'Invalid file type. Only PDF, TXT and DOCX are allowed.';
              setError(msg);
              toast.error(msg);
              return;
            }
            setError(null);
            setFileUploadingField(field.name);
            uploadPublicFormFile(token, file)
              .then((res) => {
                if (res.success && res.filename) {
                  handleChange(field, res.filename);
                }
              })
              .catch((err) => {
                const msg = err?.message || 'Upload failed';
                setError(msg);
                toast.error(msg);
              })
              .finally(() => setFileUploadingField(null));
            e.target.value = '';
          };
          return (
            <div key={field.name} className={containerClass}>
              {instructionBlock}
              <Label>{label}{requiredMark}</Label>
              {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
              <div className="flex items-center gap-2 flex-wrap">
                {filename ? (
                  <>
                    <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                      {filename.split('/').pop() || filename}
                    </span>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept={PUBLIC_FORM_FILE_ACCEPT}
                        className="sr-only"
                        disabled={isLoading}
                        onChange={handleFileChange}
                      />
                      <span className="inline-flex items-center justify-center rounded-md text-sm font-medium border border-input bg-background px-3 py-2 hover:bg-accent hover:text-accent-foreground">
                        {isLoading ? 'Uploading...' : 'Replace'}
                      </span>
                    </label>
                  </>
                ) : (
                  <label className="flex items-center gap-2 cursor-pointer hover:opacity-80">
                    <input
                      type="file"
                      accept={PUBLIC_FORM_FILE_ACCEPT}
                      className="sr-only"
                      disabled={isLoading}
                      onChange={handleFileChange}
                    />
                    {isLoading ? (
                      <span className="animate-spin rounded-full h-5 w-5 border-2 border-muted-foreground border-t-transparent flex-shrink-0" />
                    ) : (
                      <MdOutlineFileUpload className="text-2xl text-muted-foreground flex-shrink-0" />
                    )}
                    <span className="text-sm text-muted-foreground">
                      {isLoading ? 'Uploading...' : 'Choose file (PDF, TXT, DOCX)'}
                    </span>
                  </label>
                )}
              </div>
            </div>
          );
        }
        if (isLinkFieldType(field.type)) {
          return (
            <div key={field.name} className={containerClass}>
              {instructionBlock}
              <Label>{label}{requiredMark}</Label>
              {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
              <LinkFieldEditor
                fieldType={field.type}
                value={value ?? ''}
                required={field.required === true}
                onChange={(next) => handleChange(field, next)}
              />
            </div>
          );
        }
        return (
          <div key={field.name} className={containerClass}>
            {instructionBlock}
            <Label>{label}{requiredMark}</Label>
            {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
            <Input
              type="text"
              value={value ?? ''}
              onChange={(event) => handleChange(field, applyTextCaseRule(event.target.value, (field as any).case))}
            />
          </div>
        );
      })}
      {showEmailGate && (
        <div className="border rounded-md p-4 space-y-2 bg-background">
          <h2 className="text-sm font-semibold">Verify your email</h2>
          <p className="text-xs text-muted-foreground">
            We will send a login or registration link before you can submit. Please look for an email from noreply@labwick.com (and check your spam folder).
          </p>
          {emailStatus && (
            <Alert
              className={emailSent
                ? "border-green-200 bg-green-50 text-green-800"
                : "border-red-200 bg-red-50 text-red-700"}
            >
              <AlertDescription>{emailStatus}</AlertDescription>
            </Alert>
          )}
          {!emailSent && (
            <Input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && isValidEmailFormat(email) && !submitting) {
                  event.preventDefault();
                  handleEmailGate();
                }
              }}
            />
          )}
          {!emailSent && (
            <Button
              type="button"
              onClick={handleEmailGate}
              disabled={!isValidEmailFormat(email) || submitting}
              className={
                remainingSeconds > 0
                  ? "bg-blue-600 text-white hover:bg-blue-700 opacity-70 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }
            >
              {remainingSeconds > 0 ? "Wait" : "Send link"}
            </Button>
          )}
        </div>
      )}
      {!showEmailGate && (
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={submitting}
            className={remainingSeconds > 0 ? "opacity-70 cursor-not-allowed" : ""}
          >
            {submitting
              ? "Submitting..."
              : remainingSeconds > 0
                ? "Wait"
                : submitPrimaryLabel}
          </Button>
        </div>
      )}
    </form>
  );

  if (embedded) {
    return (
      <div className="overflow-y-auto overflow-x-hidden flex flex-col items-center w-full">
        {formPanel}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden bg-muted/30">
      {topBar}
      <div className="fixed top-14 left-0 right-0 bottom-0 overflow-y-auto overflow-x-hidden flex flex-col items-center py-10 px-4">
        {formPanel}
      </div>
    </div>
  );
};

export default PublicForm;
